import { createSignal } from 'solid-js';

// Which page the nav is pointing at. Declared here rather than in the page
// registry so the store stays pure data — the registry imports this for its
// completeness check, and nothing imports the registry back.
export type PageId = 'accounts' | 'exposure' | 'loans' | 'payments' | 'explore' | 'fund' | 'compare' | 'history' | 'future' | 'plan' | 'sandbox' | 'profile';

// Fund sits directly after Explore: they share a selection, so the pair reads as
// "find one" then "look at that one", and the tab order is the motion.
//
// Sandbox sits last, after Plan: a plan is kept and a sandbox is not, so the
// order runs from the real book to the disposable one.
//
// Also the nav's running order. The book first — what you own, then what you
// borrowed against it, then what those holdings actually resolve to and what
// they have paid. Then the reference pages, which are about funds rather than
// about you, and Plan last, which is not the real book at all.
export const PAGE_IDS: PageId[] = ['accounts', 'loans', 'exposure', 'payments', 'explore', 'fund', 'compare', 'history', 'future', 'plan', 'sandbox', 'profile'];

const isPageId = (value: string): value is PageId => (PAGE_IDS as string[]).includes(value);

/**
 * The hash is the source of truth: `#/page` or `#/page/detail`.
 *
 * A page survives a reload and the back button moves between them the way the
 * browser makes it look like it should. No router — two signals kept in step
 * with location.hash in both directions is the whole requirement.
 *
 * The second segment is whatever the page considers its subject. Only Explore
 * and Fund use one, and they use it for the same thing — the fund on screen —
 * which is why it is one slot here rather than a field per page: they share a
 * selection, and two slots would let the address bar disagree with itself.
 *
 * The trail through a fund of funds is deliberately not in it. That is a path
 * you took rather than a place you are, and a reload landing on the fund you
 * ended at with a fresh trail is the honest reading of a bookmark.
 */
interface Location {
    page: PageId;
    detail: string | null;
}

// Guarded, because this module is reachable from the check scripts, which run
// in Bun and have no window. Everything here degrades to "no location": the
// signals still work, the address bar just is not one of the places they are
// kept in step with.
const inBrowser = typeof window !== 'undefined';

const fromHash = (): Location | null => {
    if (!inBrowser) return null;
    const [raw, detail] = location.hash.replace(/^#\/?/, '').split('/');
    return isPageId(raw) ? { page: raw, detail: detail || null } : null;
};

const toHash = (where: Location): string => `#/${where.page}${where.detail ? `/${where.detail}` : ''}`;

const opening = fromHash();

const [page, setPageSignal] = createSignal<PageId>(opening?.page ?? 'accounts');
const [detail, setDetailSignal] = createSignal<string | null>(opening?.detail ?? null);

export { page, detail };

const write = (where: Location): void => {
    if (!inBrowser) return;
    // Assigning an unchanged hash is a no-op in the browser, but this also keeps
    // us from pushing a duplicate entry when the change came *from* a hashchange.
    const current = fromHash();
    if (current?.page !== where.page || current.detail !== where.detail) location.hash = toHash(where);
};

/** Which pages put anything in the second segment at all. */
const DETAIL_PAGES: PageId[] = ['explore', 'fund', 'compare'];

/**
 * Pages whose subject is the same subject, so moving between them keeps it.
 *
 * Explore and Fund are both about one fund. Compare is about a list of them,
 * which is a different thing wearing the same slot — carrying one into the other
 * would either drop everything but the first fund or select a whole comparison
 * from a single click, and neither is what the click meant.
 */
const SHARE_DETAIL: PageId[] = ['explore', 'fund'];

export const setPage = (next: PageId): void => {
    // Otherwise the subject is dropped: a fund symbol means nothing on Loans,
    // and leaving it in the bar would make the address describe a page it is
    // not on.
    const keep = SHARE_DETAIL.includes(next) && SHARE_DETAIL.includes(page());
    setPageSignal(next);
    if (!keep) setDetailSignal(null);
    write({ page: next, detail: keep ? detail() : null });
};

/** The page's subject — the fund on Explore and Fund, the selection on Compare.
    Set by whatever owns that state; the hash follows rather than leads. */
export const setDetail = (next: string | null): void => {
    if (detail() === next) return;
    setDetailSignal(next);
    // replace, not assign: choosing a fund is not a navigation you want twenty
    // of in the back stack after clicking down a holdings list.
    if (inBrowser && DETAIL_PAGES.includes(page())) history.replaceState(null, '', toHash({ page: page(), detail: next }));
};

export const initializeNav = (): void => {
    if (!inBrowser) return;
    // Stamps the opening location into an empty or junk hash, so the address bar
    // agrees with the screen from the first paint rather than only after a click.
    const current = fromHash();
    if (current?.page !== page() || current.detail !== detail()) location.replace(toHash({ page: page(), detail: detail() }));

    window.addEventListener('hashchange', () => {
        const next = fromHash();
        // Setting the signals directly: the hash is already what it should be,
        // and going through the setters would write it a second time.
        if (next) {
            setPageSignal(next.page);
            setDetailSignal(next.detail);
        } else {
            write({ page: page(), detail: detail() });
        }
    });
};
